import { useId, useState } from 'react';
import { NJAvatar, NJAvatarList, NJButton, NJIconButton, NJMenu, NJModal, NJModalContent, NJPopover, NJText } from '@engie-group/fluid-design-system-react';
import { UMIcon } from '../../urbanmap/_components';
import { CURRENT_USER, person } from './homeModel';
import { useShareProject } from './hooks';
import { SHARE_DIRECTORY, SHARE_ROLE_LABEL, sharingProfile, type ShareRole } from './sharing';
import type { ProjectSummary } from './types';
import './wireframes.css';

function AccessRoleMenu({ label, role, disabled, onChange, outlined = false }: {
  label: string; role: ShareRole; disabled: boolean; onChange: (role: ShareRole) => void; outlined?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <NJMenu.Root opened={open} onOpen={setOpen}>
      <NJMenu.Anchor>
        <button type="button" className={`pcv3-access-role-button${outlined ? ' pcv3-access-role-button--outlined' : ''}`} aria-label={`${label} : ${SHARE_ROLE_LABEL[role]}`}
          disabled={disabled}>
          <NJText as="span" scale="sm">{SHARE_ROLE_LABEL[role]}</NJText>
          <UMIcon name="expand_more" size={16} />
        </button>
      </NJMenu.Anchor>
      <NJMenu.Dropdown>
        <NJMenu.Group>
          {(['read', 'write'] as const).map((value) => (
            <NJMenu.Item key={value} label={SHARE_ROLE_LABEL[value]} selected={role === value}
              onClick={() => { setOpen(false); onChange(value); }}>{SHARE_ROLE_LABEL[value]}</NJMenu.Item>
          ))}
        </NJMenu.Group>
      </NJMenu.Dropdown>
    </NJMenu.Root>
  );
}

export function ProjectAccessAvatars({ project, sharing = false }: { project: ProjectSummary; sharing?: boolean }) {
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState('');
  const [role, setRole] = useState<ShareRole>('read');
  const profile = sharingProfile(project);
  const [notice, setNotice] = useState('');
  const share = useShareProject(project.id);
  const changeRole = useShareProject(project.id, 'update');
  const busy = share.isPending || changeRole.isPending;
  const titleId = useId();
  const people = [...new Set([project.owner, ...project.members])];
  const candidates = SHARE_DIRECTORY.filter((name) => !people.includes(name) && name.toLocaleLowerCase('fr').includes(query.trim().toLocaleLowerCase('fr')));
  const manage = profile !== 'read';
  const toggle = () => { if (!busy) setOpen((value) => !value); };
  const closeModal = () => { if (!busy) setModalOpen(false); };
  const accessList = (detailed: boolean) => (
    <ul className={`pcv3-access-list${detailed ? ' pcv3-share-access-list' : ''}`}>
      {people.map((name) => (
        <li className="pcv3-access-person" key={name}>
          <NJAvatar.Root scale="sm" initials={person(name).initials} label={name} />
          <div className="pcv3-access-person-text">
            <NJText as="strong" scale="sm">{name}{sharing && name === CURRENT_USER ? ' (vous)' : ''}</NJText>
          </div>
          {name !== project.owner && manage ? (
            <AccessRoleMenu label={`Droit de ${name}`} role={project.memberRoles?.[name] ?? 'read'} disabled={busy}
              onChange={(nextRole) => {
                if (nextRole === (project.memberRoles?.[name] ?? 'read')) return;
                setNotice(''); share.reset();
                changeRole.mutate({ name, role: nextRole, profile }, {
                  onSuccess: () => setNotice(`${name} : ${SHARE_ROLE_LABEL[nextRole].toLowerCase()} · droit enregistré`),
                });
              }} />
          ) : <NJText scale="sm">{name === project.owner ? 'Propriétaire' : SHARE_ROLE_LABEL[project.memberRoles?.[name] ?? 'read']}</NJText>}
        </li>
      ))}
    </ul>
  );

  const invite = (name = selected, grantedRole = role) => {
    if (!name || !manage || busy) return;
    setNotice(''); changeRole.reset();
    share.mutate({ name, role: grantedRole, profile }, {
      onSuccess: () => { setNotice(`${name} : ${SHARE_ROLE_LABEL[grantedRole].toLowerCase()} · partage simulé enregistré`); setSelected(''); setQuery(''); },
    });
  };

  const invitationForm = (quick = false) => (
    <div className="pcv3-share-form" aria-busy={busy}>
      {manage ? (
        <>
          <div className={quick ? 'pcv3-share-quick-search' : 'pcv3-share-invite'}>
            <div className="pcv3-share-search">
              <NJText as="label" scale="sm" className="pcv3-share-person-field">
                <UMIcon name="person_add" size={16} />
                <input type="text" aria-label="Ajouter une personne" placeholder="Ajouter une personne"
                  autoFocus value={query} disabled={busy}
                  onChange={(event) => { setQuery(event.target.value); setSelected(''); share.reset(); changeRole.reset(); setNotice(''); }} />
              </NJText>
            </div>
            {!quick && <>
              <AccessRoleMenu label="Droit à attribuer" role={role} disabled={busy} onChange={setRole} outlined />
              <NJButton label={share.isPending ? 'Invitation…' : 'Inviter'} scale="sm"
                disabled={!selected || busy} onClick={() => invite()} />
            </>}
          </div>
          {query && !selected && (
            <div className="pcv3-share-candidates" role="group" aria-label="Personnes disponibles">
              {candidates.map((name) => (
                <NJButton key={name} label={name} variant="secondary" scale="sm" disabled={busy}
                  icon="person_add" onClick={() => {
                    if (quick) invite(name, 'read');
                    else { setSelected(name); setQuery(name); share.reset(); changeRole.reset(); setNotice(''); }
                  }} />
              ))}
              {!candidates.length && <NJText scale="sm">Aucune personne disponible ne correspond.</NJText>}
            </div>
          )}
        </>
      ) : <NJText scale="sm">Seuls le propriétaire et les personnes ayant un accès écriture peuvent partager ce projet.</NJText>}
      {share.isError && <NJText role="alert" scale="sm">Le partage n’a pas pu être confirmé ({share.error.message}). Vérifiez les accès puis réessayez.</NJText>}
      {changeRole.isError && <NJText role="alert" scale="sm">Le droit n’a pas pu être modifié ({changeRole.error.message}). Réessayez depuis la liste des accès.</NJText>}
      {notice && <NJText role="status" scale="sm">{notice}</NJText>}
    </div>
  );

  return (
    <div className="pcv3-access-avatars pcv3-project-sharing">
    <NJPopover.Root opened={open} onOpen={(value) => { if (!busy) setOpen(value); }} useFloatingOptions={{
      placement: 'bottom-end', strategy: 'fixed',
      middleware: [{
        name: 'viewport',
        fn: ({ x, y, rects }) => ({
          x: Math.max(0, Math.min(x, document.documentElement.clientWidth - rects.floating.width)),
          y: Math.max(0, Math.min(y, window.innerHeight - rects.floating.height)),
        }),
      }],
    }}>
      <NJPopover.Interactions dismiss role={{ role: 'dialog' }}>
        <NJPopover.Anchor>
          <div className="pcv3-access-avatars pcv3-project-sharing">
            <NJAvatarList max={2} size="sm" density="default"
              showMoreLabel={(count) => `Voir les personnes ayant accès à ${project.name} · ${count} autres`}
              onClick={toggle}>
              {people.map((name) => (
                <NJAvatar.Root key={name} scale="sm" isClickable initials={person(name).initials}
                  label={`Accès de ${name} · ${project.name}`} aria-expanded={open} aria-haspopup="dialog"
                  onClick={toggle} />
              ))}
            </NJAvatarList>
          </div>
        </NJPopover.Anchor>
        <NJPopover.Content focusManagerProps={{ modal: false }}>
          <div className="pcv3-access-popover" aria-labelledby={titleId}>
            <div className="pcv3-access-heading">
              <div>
                <NJText as="strong" scale="lg" id={titleId}>Qui a accès</NJText>
                <NJText scale="xs">{people.length} accès · {people.length === 1 ? 'projet privé' : 'accès restreint'}</NJText>
              </div>
              <NJIconButton icon="close" label="Fermer la liste des accès" variant="tertiary" scale="sm"
                isDisabled={busy} onClick={() => setOpen(false)} />
            </div>
            {invitationForm(true)}
            {accessList(false)}
            <div className="pcv3-access-footer">
              <NJButton label="Gérer l’accès" variant="secondary" scale="sm" disabled={busy}
                onClick={() => { setOpen(false); setModalOpen(true); }} />
            </div>
          </div>
        </NJPopover.Content>
      </NJPopover.Interactions>
    </NJPopover.Root>
    {sharing && <NJButton label="Partager" icon="lock" scale="sm" aria-haspopup="dialog" aria-expanded={modalOpen}
      disabled={busy} onClick={() => { setOpen(false); setModalOpen(true); }} />}
      <NJModal isOpen={modalOpen} setIsOpen={(value) => { if (!busy) setModalOpen(value); }}
        closeOnEscape={!busy} closeOnBackdropClick={!busy}>
        <NJModalContent id={`${titleId}-share`} title={`Partager «\u00a0${project.name}\u00a0»`}
          className="pcv3-share-modal" closeLabel="Fermer le partage" onClose={closeModal}>
          <div className="nj-modal__body pcv3-share-body" aria-busy={busy}>
            {invitationForm()}
            <section className="pcv3-share-section" aria-label="Personnes avec accès">
              <NJText as="strong" scale="xs">PERSONNES AVEC ACCÈS</NJText>
              {accessList(true)}
            </section>
            <section className="pcv3-share-section" aria-label="Accès général">
              <NJText as="strong" scale="xs">ACCÈS GÉNÉRAL</NJText>
              <div className="pcv3-share-general">
                <UMIcon name="lock" size={24} />
                <div>
                  <NJText as="strong" scale="sm">{people.length === 1 ? 'Privé' : 'Restreint'}</NJText>
                  <NJText scale="xs">{people.length === 1 ? 'Seul le propriétaire peut ouvrir ce projet.' : 'Seules les personnes listées peuvent ouvrir ce projet.'}</NJText>
                </div>
              </div>
            </section>
          </div>
        </NJModalContent>
      </NJModal>
    </div>
  );
}
